import { useCallback, useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiError, downloadPdf } from "../lib/api";
import { DataTable, StatusBadge, fmt, waLink } from "../components/bits";
import { Button } from "../components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Download, MessageCircle } from "lucide-react";

export default function GuardianDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);

  const load = useCallback(() => {
    api.get(`/guardians/${id}/detail`).then((r) => setData(r.data)).catch((e) => toast.error(formatApiError(e)));
  }, [id, formatApiError]);
  useEffect(() => { load(); }, [load]);

  if (!data) return <p className="text-slate-400 text-sm">Cargando ficha del tutor…</p>;
  const g = data.guardian;

  return (
    <div data-testid="guardian-detail-page">
      <div className="bg-white border border-slate-200 rounded-xl p-6 shadow-sm mb-6">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-slate-900" data-testid="guardian-name">{g.name} {g.last_name}</h1>
            <p className="text-sm text-slate-500 mt-1">{g.relationship} · {g.document || "Sin documento"}</p>
            <div className="flex flex-wrap gap-2 mt-3">
              <StatusBadge value={g.is_primary ? "activo" : "pendiente"} />
              {data.balance_pending > 0 && (
                <span className="text-xs font-medium px-2.5 py-0.5 rounded-full bg-rose-50 text-rose-700 border border-rose-200" data-testid="guardian-balance-badge">
                  Debe {fmt(data.balance_pending)}
                </span>
              )}
            </div>
          </div>
          {g.whatsapp && (
            <a href={waLink(g.whatsapp, `Hola ${g.name}, le contactamos del colegio.`)} target="_blank" rel="noreferrer">
              <Button variant="outline" data-testid="guardian-whatsapp-button">
                <MessageCircle className="w-4 h-4 mr-1" /> WhatsApp
              </Button>
            </a>
          )}
        </div>
        <div className="grid sm:grid-cols-3 gap-4 mt-5 text-sm">
          {[["Teléfono", g.phone || "—"], ["WhatsApp", g.whatsapp || "—"], ["Correo", g.email || "—"], ["Dirección", g.address || "—"],
            ["Contacto autorizado", g.is_authorized ? "Sí" : "No"], ["Estudiantes", data.students.length]].map(([k, v]) => (
            <div key={k}><p className="text-xs uppercase tracking-wider text-slate-400">{k}</p><p className="text-slate-800 mt-0.5">{v}</p></div>
          ))}
        </div>
      </div>

      <Tabs defaultValue="students">
        <TabsList className="flex flex-wrap h-auto gap-1 mb-4">
          <TabsTrigger value="students" data-testid="gtab-students">Estudiantes ({data.students.length})</TabsTrigger>
          <TabsTrigger value="invoices" data-testid="gtab-invoices">Facturas ({data.invoices.length})</TabsTrigger>
          <TabsTrigger value="payments" data-testid="gtab-payments">Pagos ({data.payments.length})</TabsTrigger>
        </TabsList>

        <TabsContent value="students">
          <DataTable testid="guardian-students-table" rows={data.students} onRowClick={(s) => navigate(`/estudiantes/${s.id}`)} columns={[
            { key: "name", label: "Estudiante", render: (s) => <span className="font-medium text-slate-900">{s.first_name} {s.last_name}</span> },
            { key: "grade", label: "Curso" },
            { key: "section", label: "Sección" },
            { key: "status", label: "Estado", render: (s) => <StatusBadge value={s.status} /> },
          ]} />
        </TabsContent>

        <TabsContent value="invoices">
          <DataTable testid="guardian-invoices-table" rows={data.invoices} columns={[
            { key: "number", label: "Factura", render: (i) => <span className="font-mono text-xs font-semibold">{i.number}</span> },
            { key: "student_name", label: "Estudiante" },
            { key: "issue_date", label: "Fecha" },
            { key: "total", label: "Total", render: (i) => fmt(i.total) },
            { key: "status", label: "Estado", render: (i) => <StatusBadge value={i.status} /> },
            {
              key: "_a", label: "",
              render: (i) => (
                <Button size="sm" variant="ghost" data-testid={`guardian-invoice-pdf-${i.id}`} onClick={async (e) => { e.stopPropagation(); try { await downloadPdf(`/invoices/${i.id}/pdf`, `${i.number}.pdf`); } catch (err) { toast.error(formatApiError(err)); } }}>
                  <Download className="w-4 h-4" />
                </Button>
              ),
            },
          ]} />
        </TabsContent>

        <TabsContent value="payments">
          <DataTable testid="guardian-payments-table" rows={data.payments} columns={[
            { key: "invoice_number", label: "Factura", render: (p) => <span className="font-mono text-xs">{p.invoice_number}</span> },
            { key: "amount", label: "Monto", render: (p) => fmt(p.amount) },
            { key: "method", label: "Método" },
            { key: "reference", label: "Referencia" },
            { key: "status", label: "Estado", render: (p) => <StatusBadge value={p.status} /> },
          ]} />
        </TabsContent>
      </Tabs>
    </div>
  );
}
